import { axiosPrivate } from "@/api/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function useProductCategoryMutations({
  resetForm,
  closeAdd,
  closeEdit,
  clearEditingId,
  closeConfirm,
}: {
  resetForm: () => void;
  closeAdd: () => void;
  closeEdit: () => void;
  clearEditingId: () => void;
  closeConfirm: () => void;
}) {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (newCategory: { name: string }) => {
      const res = await axiosPrivate.post("/categories/", newCategory);
      const c = res.data.data;
      return { id: Number(c.id ?? c.category_id), name: c.name };
    },
    onSuccess: (created) => {
      queryClient.setQueryData(["productCategories"], (old: any[] = []) => [
        ...old,
        created,
      ]);
      toast.success("Product category created");
      resetForm();
      closeAdd();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to create product category");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (cat: { id: number; name: string }) => {
      const res = await axiosPrivate.put(`/categories/${cat.id}/`, cat);
      const c = res.data.data;
      return { id: Number(c.id ?? c.category_id), name: c.name };
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["productCategories"], (old: any[] = []) =>
        old.map((c) => (c.id === updated.id ? updated : c))
      );
      toast.success("Product category updated");
      resetForm();
      closeEdit();
      clearEditingId();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to update product category");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await axiosPrivate.delete(`/categories/${id}/`);
      return id;
    },
    onSuccess: (id) => {
      let removed = false;
      queryClient.setQueryData(["productCategories"], (old: any[] = []) => {
        const next = old.filter((c) => c.id !== id);
        removed = next.length !== old.length;
        return next;
      });
      if (!removed) {
        queryClient.invalidateQueries({ queryKey: ["productCategories"] });
      }
      toast.success("Product category deleted");
      clearEditingId();
      closeConfirm();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to delete product category");
    },
  });

  return { createMutation, updateMutation, deleteMutation };
}
